import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Database, CloudCog, Network, Boxes, CheckCircle2, Bot } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { SERVICES, PRODUCTS, STATS, IMAGES } from "@/data/site";

const ICONS = { "data-engineering": Database, "cloud-migration": CloudCog, "sas-onprem": Database, networking: Network, "ai-agentic": Bot, hardware: Boxes };

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const chipY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} className="relative overflow-hidden blueprint-grid" data-testid="home-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm"
              data-testid="hero-badge"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-gray-600">
                Data Engineering · AI & Agentic AI · Cloud Migration · IT Infrastructure
              </span>
            </motion.div>
            <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[4.4rem] text-ink">
              <MaskedLine delay={0.25}>Legacy data,</MaskedLine>
              <MaskedLine delay={0.4}>
                <span className="text-cobalt italic">modernised.</span>
              </MaskedLine>
              <MaskedLine delay={0.55}>Infrastructure,</MaskedLine>
              <MaskedLine delay={0.7}>delivered end to end.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-6 text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed"
            >
              Tranzlytics migrates enterprises from SAS and on-prem databases to Snowflake and Databricks, puts Agentic AI to work on your pipelines — and powers the office behind it with networking, hardware and support.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <Link
                to="/services"
                data-testid="home-hero-cta-services"
                className="inline-flex items-center gap-2 bg-ink text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-cobalt transition-colors duration-300"
              >
                Explore Services
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="home-hero-cta-quote"
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-ink font-semibold text-sm px-7 py-3.5 rounded-full hover:border-cobalt hover:text-cobalt transition-colors duration-300"
              >
                Request a Quote
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="rounded-3xl overflow-hidden border border-gray-200 shadow-2xl shadow-gray-300/60">
                <img src={IMAGES.hero} alt="AI robot powering enterprise data" className="w-full h-[420px] object-cover" data-testid="hero-image" />
              </motion.div>
              <motion.div
                style={{ y: chipY }}
                className="absolute -left-4 sm:-left-8 top-8 bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl px-4 py-3 shadow-lg"
                data-testid="hero-chip-migration"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-500">Migration success</p>
                <p className="font-display font-extrabold text-xl text-ink mt-0.5">100%</p>
              </motion.div>
              <motion.div
                style={{ y: chipY }}
                className="absolute -right-2 sm:-right-4 bottom-10 bg-ink text-white rounded-2xl px-4 py-3 shadow-xl"
                data-testid="hero-chip-scale"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">Pipelines operated</p>
                <p className="font-display font-extrabold text-xl mt-0.5">50TB+</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Solutions = () => (
  <section className="py-20 sm:py-28" data-testid="home-solutions">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Overline>What we do</Overline>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink max-w-2xl">
          One partner from database to desktop.
        </h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.id];
          const dark = i === 1;
          return (
            <Reveal key={s.id} delay={i * 0.08} className={i === 0 ? "lg:row-span-2" : ""}>
              <Link
                to="/services"
                data-testid={`home-solution-card-${s.id}`}
                className={`group block h-full rounded-3xl border p-7 transition-all duration-300 hover:-translate-y-1.5 ${
                  dark
                    ? "bg-ink text-white border-ink shadow-xl shadow-gray-300/50"
                    : "bg-white border-gray-200 hover:border-cobalt/40 hover:shadow-xl hover:shadow-cobalt/5"
                } ${i === 0 ? "flex flex-col" : ""}`}
              >
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${dark ? "bg-cobalt" : "bg-cobalt-light"}`}>
                  <Icon className={`h-5 w-5 ${dark ? "text-white" : "text-cobalt"}`} />
                </div>
                <p className={`mt-5 font-mono text-xs tracking-[0.2em] ${dark ? "text-white/40" : "text-gray-400"}`}>{s.num}</p>
                <h3 className={`mt-2 font-display text-xl font-bold tracking-tight ${dark ? "text-white" : "text-ink"}`}>{s.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-white/60" : "text-gray-600"}`}>{s.blurb}</p>
                {i === 0 && (
                  <div className="mt-6 rounded-2xl overflow-hidden border border-gray-100 flex-1 min-h-[160px]">
                    <img src={IMAGES.dataCenter} alt="AI robotics driving data pipelines" className="w-full h-full object-cover" />
                  </div>
                )}
                <span className={`mt-5 inline-flex items-center gap-1.5 text-sm font-semibold ${dark ? "text-cobalt" : "text-cobalt"}`}>
                  Learn more
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Estimator = () => {
  const [jobs, setJobs] = useState(120);
  const [source, setSource] = useState("sas");
  const [target, setTarget] = useState("snowflake");

  const complexity = { sas: 1.3, teradata: 1.15, oracle: 1.1, sqlserver: 1.0 }[source];
  const targetFactor = { snowflake: 1.0, databricks: 1.05 }[target];
  const weeks = Math.max(4, Math.round((jobs / 18) * complexity * targetFactor));
  const savings = Math.min(80, Math.round(42 + jobs * 0.07 + (complexity - 1) * 30));

  return (
    <section className="py-20 sm:py-28 bg-ink text-white relative grain overflow-hidden" data-testid="home-estimator">
      <div className="blueprint-grid-dark absolute inset-0 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline light>Migration estimator</Overline>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              What would your SAS-to-cloud move look like?
            </h2>
            <p className="mt-4 text-white/60 leading-relaxed max-w-lg">
              A quick directional estimate based on the migration programmes we have run — 200+ legacy SAS programs modernised, up to 80% run-cost reduction delivered.
            </p>
            <div className="mt-10 space-y-7">
              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label className="font-mono text-xs uppercase tracking-[0.18em] text-white/50">Legacy programs / jobs</label>
                  <span className="font-display font-extrabold text-2xl text-cobalt" data-testid="estimator-jobs-value">{jobs}</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="500"
                  step="10"
                  value={jobs}
                  data-testid="estimator-jobs-slider"
                  onChange={(e) => setJobs(Number(e.target.value))}
                  className="tranz-range w-full"
                  style={{ "--fill": `${((jobs - 10) / 490) * 100}%` }}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono text-xs uppercase tracking-[0.18em] text-white/50 block mb-3">Source platform</label>
                  <div className="flex flex-wrap gap-2" data-testid="estimator-source-group">
                    {[["sas", "SAS"], ["teradata", "Teradata"], ["oracle", "Oracle"], ["sqlserver", "SQL Server"]].map(([id, label]) => (
                      <button
                        key={id}
                        data-testid={`estimator-source-${id}`}
                        onClick={() => setSource(id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          source === id ? "bg-cobalt text-white" : "bg-white/10 text-white/70 hover:bg-white/20"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-[0.18em] text-white/50 block mb-3">Target cloud</label>
                  <div className="flex flex-wrap gap-2" data-testid="estimator-target-group">
                    {[["snowflake", "Snowflake"], ["databricks", "Databricks"]].map(([id, label]) => (
                      <button
                        key={id}
                        data-testid={`estimator-target-${id}`}
                        onClick={() => setTarget(id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          target === id ? "bg-cobalt text-white" : "bg-white/10 text-white/70 hover:bg-white/20"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="rounded-3xl bg-white/[0.06] border border-white/10 p-8" data-testid="estimator-result-timeline">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-white/50">Estimated timeline</p>
                <p className="mt-4 font-display font-extrabold text-5xl sm:text-6xl tracking-tight text-white">
                  {weeks}
                  <span className="text-xl text-white/40 font-bold ml-2">wks</span>
                </p>
                <p className="mt-4 text-sm text-white/50 leading-relaxed">Factory-model conversion, testing and parallel-run included.</p>
              </div>
              <div className="rounded-3xl bg-cobalt p-8" data-testid="estimator-result-savings">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-white/70">Run-cost reduction</p>
                <p className="mt-4 font-display font-extrabold text-5xl sm:text-6xl tracking-tight">
                  ~{savings}
                  <span className="text-xl text-white/70 font-bold ml-1">%</span>
                </p>
                <p className="mt-4 text-sm text-white/80 leading-relaxed">Through in-database processing and warehouse right-sizing.</p>
              </div>
              <Link
                to="/contact"
                data-testid="estimator-cta-detailed"
                className="sm:col-span-2 group inline-flex items-center justify-center gap-2 bg-white text-ink font-semibold text-sm px-7 py-4 rounded-full hover:bg-cobalt hover:text-white transition-colors duration-300"
              >
                Get a detailed migration assessment
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ProductsTeaser = () => (
  <section className="py-20 sm:py-28" data-testid="home-products-teaser">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <Reveal>
          <Overline>Hardware supply</Overline>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink max-w-xl">
            The office stack, procured and supported.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/products"
            data-testid="home-products-view-all"
            className="inline-flex items-center gap-2 text-sm font-semibold text-cobalt hover:text-cobalt-hover transition-colors"
          >
            View full catalogue
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {PRODUCTS.slice(0, 3).map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <Link
              to="/products"
              data-testid={`home-product-teaser-${p.id}`}
              className="group block rounded-3xl overflow-hidden border border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-gray-200"
            >
              <div className="overflow-hidden h-56">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="font-display text-lg font-bold tracking-tight text-ink">{p.name}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{p.blurb}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="border-y border-gray-200 bg-white py-16" data-testid="home-stats">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <p className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-ink" data-testid={`home-stat-value-${i}`}>
            {s.value}
          </p>
          <p className="mt-2 text-sm text-gray-500 leading-snug">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const CtaBand = () => (
  <section className="py-20 sm:py-28" data-testid="home-cta-band">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="relative rounded-[2rem] bg-cobalt text-white overflow-hidden grain p-10 sm:p-16">
          <div className="blueprint-grid-dark absolute inset-0 pointer-events-none" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Ready to modernise your data — and your office?
            </h2>
            <p className="mt-5 text-white/80 leading-relaxed">
              Tell us about your migration, networking or hardware requirement. We respond within one business day.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="inline-flex items-center gap-2 bg-white text-cobalt font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-ink hover:text-white transition-colors duration-300"
              >
                Talk to us
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const WhyUs = () => (
  <section className="pb-20 sm:pb-28" data-testid="home-why-us">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="rounded-3xl overflow-hidden border border-gray-200">
            <img src={IMAGES.team} alt="Tranzlytics modern technology workspace" className="w-full h-[380px] object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <Overline>Why Tranzlytics</Overline>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
            Engineered by a team that has run the migrations we sell.
          </h2>
          <ul className="mt-7 space-y-4">
            {[
              "Senior-led delivery across enterprise data platforms",
              "200+ legacy SAS programs converted to Python, Snowflake & Databricks",
              "In-database optimisation first — up to 80% lower run costs",
              "One vendor for data, network and hardware — no hand-off gaps",
            ].map((point) => (
              <li key={point} className="flex items-start gap-3 text-gray-700 text-sm sm:text-base">
                <CheckCircle2 className="h-5 w-5 text-cobalt shrink-0 mt-0.5" />
                {point}
              </li>
            ))}
          </ul>
          <Link
            to="/about"
            data-testid="home-why-us-cta"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-cobalt hover:text-cobalt-hover transition-colors"
          >
            More about us
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <Solutions />
      <Estimator />
      <Stats />
      <ProductsTeaser />
      <WhyUs />
      <CtaBand />
    </div>
  );
}
